import React from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "config/navConfig";

const COMPLIANCE_ITEMS = [
  { label: "Organisation Details", icon: "Building2", field: "org" },
  { label: "Investor Classification", icon: "Users", field: "type" },
  { label: "Jurisdiction Verification", icon: "Globe", field: "jurisdiction" },
  { label: "KYC / AML Documents", icon: "FileCheck", field: "kyc" },
  { label: "Accreditation Check", icon: "ShieldCheck", field: "accreditation" },
];

const REGULATORY_ACKNOWLEDGEMENTS = [
  "FCA Authorised Platform — FRN 987654",
  "GDPR Compliant Data Processing",
  "ISO 27001 Certified Infrastructure",
  "AML / CTF Policy Compliant",
  "MiFID II Professional Client Framework",
];

export default function ComplianceSummaryPanel({ formData }) {
  const navigate = useNavigate();
  const today = new Date();
  const formattedDate = today?.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const getStatus = (field) => {
    switch (field) {
      case "org":
        return formData?.organisation?.legalName ? "complete" : "pending";
      case "type":
        return formData?.investorType?.investorType ? "complete" : "pending";
      case "jurisdiction":
        return formData?.jurisdiction?.jurisdiction ? "complete" : "pending";
      case "kyc":
        return formData?.kyc?.attestAccuracy ? "complete" : "pending";
      case "accreditation":
        return formData?.accreditation?.accreditationVerified ? "complete" : "pending";
      default:
        return "pending";
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-8">
      <div className="w-full max-w-2xl">
        {/* Success header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-4">
            <Icon name="CheckCircle2" size={44} color="var(--color-success)" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-success mb-2">
            Application Approved
          </h1>
          <p className="text-muted-foreground font-caption">
            Your investor onboarding has been successfully completed. Welcome to SeedDrive.
          </p>
        </div>

        {/* Compliance summary card */}
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-foreground">Compliance Summary</h2>
            <span className="font-data text-xs text-muted-foreground">{formattedDate}</span>
          </div>

          <div className="space-y-3 mb-6">
            {COMPLIANCE_ITEMS?.map((item) => {
              const status = getStatus(item?.field);
              return (
                <div key={item?.field} className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                  <div className="flex-shrink-0 w-8 h-8 rounded-md bg-card flex items-center justify-center">
                    <Icon name={item?.icon} size={16} color="var(--color-secondary)" />
                  </div>
                  <p className="flex-1 font-caption text-sm text-foreground">{item?.label}</p>
                  <div className="flex items-center gap-1">
                    <Icon
                      name={status === "complete" ? "CheckCircle2" : "Clock"}
                      size={16}
                      color={status === "complete" ? "var(--color-success)" : "var(--color-warning)"}
                    />
                    <span
                      className={`text-xs font-caption font-semibold ${
                        status === "complete" ? "text-success" : "text-warning"
                      }`}
                    >
                      {status === "complete" ? "Verified" : "Pending"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Regulatory acknowledgements */}
          <div className="pt-4 border-t border-border">
            <p className="font-caption text-xs text-muted-foreground mb-3 uppercase tracking-wide">
              Regulatory Acknowledgements
            </p>
            <div className="flex flex-wrap gap-2">
              {REGULATORY_ACKNOWLEDGEMENTS?.map((ack) => (
                <span
                  key={ack}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[rgba(59,76,76,0.07)] text-primary text-xs font-caption border border-blue-100"
                >
                  <Icon name="Shield" size={10} />
                  {ack}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Reference number */}
        <div className="p-4 rounded-lg border border-border bg-card mb-6 text-center">
          <p className="text-xs text-muted-foreground font-caption mb-1">Onboarding Reference Number</p>
          <p className="font-data text-lg font-semibold text-primary">SD-INV-2026-00847</p>
          <p className="text-xs text-muted-foreground mt-1">
            Please retain this reference for your records. A confirmation has been sent to your registered email.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="default"
            size="lg"
            fullWidth
            iconName="LayoutDashboard"
            iconPosition="left"
            onClick={() => navigate(ROUTES.dashboard)}
          >
            Go to Portfolio Dashboard
          </Button>
          <Button
            variant="outline"
            size="lg"
            fullWidth
            iconName="TrendingUp"
            iconPosition="left"
            onClick={() => navigate(ROUTES.opportunities)}
          >
            Browse Opportunities
          </Button>
        </div>
      </div>
    </div>
  );
}